import { ref, onUpdated } from 'vue';

/**
 * Composable for keyboard navigation in menu lists (replaces Navigable mixin)
 *
 * @param {Object} options - Configuration options
 * @param {Ref} options.checked - Ref to array of checked items
 * @param {Function} options.removeSelection - Function to remove selected items
 * @param {Ref} options.listRef - Template ref to the menu-list element
 */
export function useKeyboardNav(options = {}) {
    const { checked, removeSelection, listRef } = options;

    /**
     * Focus the active item after component updates
     * Call this in onUpdated or watch as needed
     */
    function focusActiveItem() {
        if (!listRef?.value) return;

        // listRef is the menu-list element itself, so query directly for links
        const target = listRef.value.querySelector('a.is-active');
        if (target) {
            target.focus();
        }
    }

    // Auto-focus on updates if listRef is provided
    if (listRef) {
        onUpdated(() => {
            focusActiveItem();
        });
    }

    /**
     * Handle keydown events for navigation
     * @param {KeyboardEvent} e - The keyboard event
     */
    function onKeydown(e) {
        if (!listRef?.value) return;

        // listRef is the menu-list element itself, so query directly
        let target = listRef.value.querySelector('a.is-active');
        if (!target) {
            target = listRef.value.querySelector('a');
        }

        if (!target) return;

        switch (e.code) {
            case 'Enter':
                e.preventDefault();
                break;

            case 'Delete':
            case 'Backspace':
                if (removeSelection) {
                    removeSelection();
                }
                e.preventDefault();
                break;

            case 'Space':
                const input = target.parentElement.querySelector('input');
                if (input) {
                    input.click();
                }
                e.preventDefault();
                break;

            case 'Escape':
                if (checked) {
                    checked.value = [];
                }
                e.preventDefault();
                break;

            case 'ArrowDown':
            case 'ArrowUp':
            case 'PageDown':
            case 'PageUp':
                navTo(e, target);
                e.preventDefault();
                break;
        }
    }

    /**
     * Navigate to adjacent item based on key pressed
     * @param {KeyboardEvent} e - The keyboard event
     * @param {HTMLElement} target - Current active element
     */
    function navTo(e, target) {
        if (!listRef?.value) return;

        // listRef is the menu-list element itself
        const menuList = listRef.value;

        const listH = menuList.clientHeight;
        const listTop = menuList.scrollTop;
        const firstLi = menuList.querySelector('li');
        const liH = firstLi ? firstLi.clientHeight * 2 : 40;
        let oT = 0;
        let followScroll = false;

        if (e.code === 'ArrowDown') {
            let el = target.parentElement.nextElementSibling;
            // Skip group headers (li without links)
            while (el && !el.querySelector('a')) {
                el = el.nextElementSibling;
            }
            if (el) {
                const a = el.querySelector('a');
                oT = el.offsetTop;
                a.focus();
                a.click();
                followScroll = true;
            }
        } else if (e.code === 'ArrowUp') {
            let el = target.parentElement.previousElementSibling;
            // Skip group headers (li without links)
            while (el && !el.querySelector('a')) {
                el = el.previousElementSibling;
            }
            if (el) {
                const a = el.querySelector('a');
                oT = el.offsetTop;
                a.focus();
                a.click();
                followScroll = true;
            }
        } else if (e.code === 'PageDown') {
            let el = target.parentElement;
            while ((el = el.nextElementSibling) && el.tagName !== 'DIV') {
                if (el.offsetTop > listTop + listH || el.nextElementSibling?.tagName === 'DIV') {
                    const a = el.querySelector('a');
                    if (a) {
                        oT = el.offsetTop;
                        a.focus();
                        a.click();
                        followScroll = true;
                    }
                    break;
                }
            }
        } else if (e.code === 'PageUp') {
            let el = target.parentElement;
            while ((el = el.previousElementSibling)) {
                if (el.offsetTop <= listTop) {
                    const a = el.querySelector('a');
                    if (a) {
                        oT = el.offsetTop;
                        a.focus();
                        a.click();
                        followScroll = true;
                    }
                    break;
                }
            }
        }

        if (followScroll && (oT > listTop + listH - liH || oT < listTop - listH)) {
            menuList.scrollTop = oT - listH + listH / 2;
        }
    }

    return {
        onKeydown,
        focusActiveItem
    };
}
